import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { FullPageLoader } from './LoadingSpinner';
import Layout from './Layout';
import DDPCreatePage from '../pages/DDPCreatePage';

/**
 * Gate for /ddp/create:
 * - Not logged in: show "Create client account" and "Sign in" links, then redirect back here after login.
 * - Logged in CLIENT with status pending: show "Account pending manager approval".
 * - Logged in CLIENT active: show DDP create form.
 * - Logged in other role: show access denied.
 */
const DDPCreateGate = () => {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const returnToCreate = { pathname: '/ddp/create', search: location.search };

  if (loading) {
    return <FullPageLoader message={t('common.loading')} />;
  }

  // Not logged in: show gate with register and login links
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full space-y-8 text-center">
            <div>
              <img
                src="/logo_cciad.jpg"
                alt="CCIAD"
                className="mx-auto h-20 w-auto object-contain max-h-24 sm:max-h-28"
              />
              <p className="mt-3 text-sm font-medium text-gray-600">
                CCIAD | Chambre de Commerce, d'Industrie et d'Agriculture de Dakar
              </p>
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                {t('ddp.createTitle')}
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                {t('ddp.gateSignInOrRegister')}
              </p>
            </div>
            <div className="space-y-3">
              <Link
                to="/login"
                state={{ from: returnToCreate }}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {t('login.signIn')}
              </Link>
              <p className="text-sm text-gray-500">{t('ddp.gateOr')}</p>
              <Link
                to="/operator/register"
                state={{ from: returnToCreate }}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {t('ddp.gateCreateOperatorAccount')}
              </Link>
            </div>
          </div>
        </div>
        <footer className="bg-white border-t border-gray-200 py-4">
          <div className="text-center text-sm text-gray-500">
            © {new Date().getFullYear()} Toubanetwork
          </div>
        </footer>
      </div>
    );
  }

  // Logged in but not CLIENT, ADMIN, MANAGER, or PESEUR
  if (user.role !== 'CLIENT' && user.role !== 'ADMIN' && user.role !== 'MANAGER' && user.role !== 'PESEUR') {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('ddp.gateAccessDenied')}</h1>
            <p className="text-gray-600">{t('ddp.gateOperatorOnly')}</p>
            <Link to="/dashboard" className="mt-4 inline-block text-blue-600 hover:underline">
              {t('navigation.dashboard')}
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  // CLIENT, ADMIN, MANAGER, or PESEUR: show create form (PESEUR: paying client + assigned weighbridge; ADMIN/MANAGER: operator)
  return (
    <Layout>
      <DDPCreatePage />
    </Layout>
  );
};

export default DDPCreateGate;
